import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { usePageTitle as usePageTitleContext } from '../contexts/PageTitleContext';
import { useAuth } from '../contexts/AuthContext';

interface TranslationData {
  [key: string]: any;
}

const TranslationDetail: React.FC = () => {
  const { langId } = useParams<{ langId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageTitle } = usePageTitleContext();
  const { user } = useAuth();
  const isSysAdmin = user?.role === 'sysadmin';
  const isTranslator = !!(user?.role && user.role.startsWith('translator_'));

  // Get channel from URL, default to 'web'
  // Translators always use 'web' channel (no channel toggle for them)
  const channelParam = isSysAdmin ? (searchParams.get('channel') || 'web') : 'web';
  const channel = (channelParam === 'desktop' ? 'desktop' : 'web') as 'web' | 'desktop';

  const [translations, setTranslations] = useState<{ [namespace: string]: TranslationData }>({});
  const [editedTranslations, setEditedTranslations] = useState<{ [namespace: string]: TranslationData }>({});
  const [isFromDB, setIsFromDB] = useState<{ [namespace: string]: boolean }>({});
  const [selectedNamespace, setSelectedNamespace] = useState<string>('common');
  const [isImporting, setIsImporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [importFormat, setImportFormat] = useState<'zip' | 'excel'>('zip');
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showExportNamespaceMenu, setShowExportNamespaceMenu] = useState(false);
  const [showExportAllMenu, setShowExportAllMenu] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const importMenuRef = useRef<HTMLDivElement>(null);
  const exportNamespaceMenuRef = useRef<HTMLDivElement>(null);
  const exportAllMenuRef = useRef<HTMLDivElement>(null);

  const handleChannelChange = (newChannel: 'web' | 'desktop') => {
    setSearchParams({ channel: newChannel });
  };

  const languageNames: { [key: string]: string } = {
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    fr: 'Français',
    fi: 'Suomi',
    tr: 'Türkçe',
  };

  const flagMap: { [key: string]: string } = {
    de: '🇩🇪',
    es: '🇪🇸',
    fr: '🇫🇷',
    fi: '🇫🇮',
    tr: '🇹🇷',
    en: '🇺🇸',
  };

  const namespaces = ['common', 'login', 'forgotPassword', 'dashboard', 'sidebar'];

  // Load translations from backend API
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const loaded: { [namespace: string]: TranslationData } = {};
        const fromDB: { [namespace: string]: boolean } = {};
        
        for (const ns of namespaces) {
          try {
            const data = await apiService.getTranslation(langId || '', ns, channel);
            if (data && data.content) {
              loaded[ns] = data.content;
              fromDB[ns] = true; // Loaded from database - editable
            } else {
              // Translation not found in database - empty and not editable
              loaded[ns] = {};
              fromDB[ns] = false;
            }
          } catch (error) {
            // Error loading from database - empty and not editable
            console.warn(`Failed to load translation ${langId}/${ns} from database:`, error);
            loaded[ns] = {};
            fromDB[ns] = false;
          }
        }
        setTranslations(loaded);
        setEditedTranslations(loaded); // Initialize edited state
        setIsFromDB(fromDB);
      } catch (error) {
        console.error('Error loading translations:', error);
        toast.error('Failed to load translations');
      }
    };

    if (langId) {
      loadTranslations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langId, channel]);

  const flattenTranslations = (obj: any, prefix = ''): Array<{ key: string; value: string }> => {
    const result: Array<{ key: string; value: string }> = [];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          result.push(...flattenTranslations(obj[key], fullKey));
        } else {
          result.push({ key: fullKey, value: String(obj[key] || '') });
        }
      }
    }
    return result;
  };

  const unflattenTranslations = (flat: Array<{ key: string; value: string }>): TranslationData => {
    const result: TranslationData = {};
    for (const { key, value } of flat) {
      const keys = key.split('.');
      let current: any = result;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    }
    return result;
  };

  const handleValueChange = (key: string, value: string) => {
    if (!selectedNamespace) return;
    
    const flat = flattenTranslations(editedTranslations[selectedNamespace] || {});
    const updated = flat.map(item => item.key === key ? { ...item, value } : item);
    const unflattened = unflattenTranslations(updated);
    
    setEditedTranslations({
      ...editedTranslations,
      [selectedNamespace]: unflattened,
    });
  };

  const handleSave = async () => {
    if (!langId || !selectedNamespace) return;
    
    if (!isFromDB[selectedNamespace]) {
      toast.error('Cannot save: This namespace is not loaded from the database. Import it first to make it editable.');
      return;
    }

    setIsSaving(true);
    try {
      const content = editedTranslations[selectedNamespace] || {};
      await apiService.updateTranslation(langId, selectedNamespace, content, channel);
      
      // Update the translations state
      setTranslations({
        ...translations,
        [selectedNamespace]: content,
      });
      
      toast.success('Translation saved successfully');
    } catch (error: any) {
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    if (!selectedNamespace) return false;
    const original = JSON.stringify(translations[selectedNamespace] || {});
    const edited = JSON.stringify(editedTranslations[selectedNamespace] || {});
    return original !== edited;
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(event.target as Node)) {
        setShowImportMenu(false);
      }
      if (exportNamespaceMenuRef.current && !exportNamespaceMenuRef.current.contains(event.target as Node)) {
        setShowExportNamespaceMenu(false);
      }
      if (exportAllMenuRef.current && !exportAllMenuRef.current.contains(event.target as Node)) {
        setShowExportAllMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // For translators, only accept Excel files
    if (isTranslator) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.XLSX')) {
        toast.error('Please select an Excel file (.xlsx)');
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    } else {
      // For sysadmins, check based on selected format
      if (importFormat === 'zip' && !file.name.endsWith('.zip')) {
        toast.error('Please select a ZIP file');
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      if (importFormat === 'excel' && !file.name.endsWith('.xlsx') && !file.name.endsWith('.XLSX')) {
        toast.error('Please select an Excel file (.xlsx)');
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    setIsImporting(true);
    try {
      // Pass langId and channel for single-language imports (files can be at root level)
      const result = await apiService.importTranslations(file, langId || undefined, channel);
      
      const message = `Import completed: ${result.inserted} inserted, ${result.updated} updated, ${result.skipped} skipped`;
      const hasErrors = result.errors && result.errors.length > 0;
      const hasWarnings = result.warnings && result.warnings.length > 0;
      if (hasErrors) {
        toast.error(`${message}. ${result.errors.length} errors occurred.`, {
          duration: 5000,
        });
        console.error('Import errors:', result.errors);
      } else {
        toast.success(hasWarnings ? `${message}. ${result.warnings.length} warnings (missing keys vs English).` : message);
        if (hasWarnings) {
          console.warn('Import warnings (missing keys):', result.warnings);
        }
      }

      // Reload translations after import (now from DB, so editable)
      if (langId) {
        const loaded: { [namespace: string]: TranslationData } = {};
        const fromDB: { [namespace: string]: boolean } = {};
        for (const ns of namespaces) {
          try {
            const data = await apiService.getTranslation(langId, ns, channel);
            if (data && data.content) {
              loaded[ns] = data.content;
              fromDB[ns] = true; // Now loaded from DB - editable
            } else {
              loaded[ns] = translations[ns] || {};
              fromDB[ns] = isFromDB[ns] || false;
            }
          } catch (error) {
            loaded[ns] = translations[ns] || {};
            fromDB[ns] = isFromDB[ns] || false;
          }
        }
        setTranslations(loaded);
        setEditedTranslations(loaded);
        setIsFromDB(fromDB);
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExport = async (exportAll: boolean = false, format?: 'zip' | 'excel') => {
    try {
      if (exportAll) {
        // Export all namespaces for this language from backend
        if (!langId) {
          toast.error('Language ID is required');
          return;
        }
        const formatParam = isSysAdmin && format ? format : undefined;
        const blob = await apiService.exportLanguage(langId, channel, formatParam);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Backend returns:
        // - ZIP for sysadmins (JSON format) or sysadmins with format=zip
        // - Single Excel file for translators or sysadmins with format=excel
        const isExcelFormat = isTranslator || (isSysAdmin && format === 'excel');
        const extension = isExcelFormat ? '.xlsx' : '.zip';
        a.download = `${langId}-translations-${channel}${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`All ${channel} translations for ${languageNames[langId]} exported successfully`);
      } else {
        // Export single namespace from backend
        if (!langId || !selectedNamespace) {
          toast.error('Language ID and namespace are required');
          return;
        }
        const formatParam = isSysAdmin && format ? format : undefined;
        const blob = await apiService.exportNamespace(langId, selectedNamespace, channel, formatParam);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Backend automatically returns correct format based on user role and format parameter
        const extension = isTranslator || (isSysAdmin && format === 'excel') ? '.xlsx' : '.json';
        a.download = `${selectedNamespace}${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Translation exported successfully');
      }
      setShowExportNamespaceMenu(false);
      setShowExportAllMenu(false);
    } catch (error: any) {
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    }
  };

  const flatTranslations = selectedNamespace && editedTranslations[selectedNamespace]
    ? flattenTranslations(editedTranslations[selectedNamespace])
    : [];
  
  const isEditable = selectedNamespace ? isFromDB[selectedNamespace] : false;
  
  // Check if there are any translations for the current channel
  const hasTranslations = Object.keys(translations).some(ns => {
    const nsTranslations = translations[ns];
    return nsTranslations && Object.keys(nsTranslations).length > 0;
  });

  // Update header with custom content
  useEffect(() => {
    const customNode = (
      <div className="flex items-center space-x-3">
        <button
          onClick={() => navigate('/translations')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <span className="text-2xl">{flagMap[langId || ''] || '🌐'}</span>
        <span className="text-xl font-semibold text-gray-900 dark:text-white">
          {languageNames[langId || ''] || langId?.toUpperCase()}
        </span>
      </div>
    );

    const rightSideButtons = (
      <div className="flex items-center space-x-2">
        {/* Channel Toggle - Only show for sysadmins */}
        {isSysAdmin && (
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => handleChannelChange('web')}
              className={`p-2 rounded-md transition-colors ${
                channel === 'web'
                  ? 'bg-white dark:bg-gray-700 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Web Channel"
            >
              <GlobeAltIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleChannelChange('desktop')}
              className={`p-2 rounded-md transition-colors ${
                channel === 'desktop'
                  ? 'bg-white dark:bg-gray-700 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Desktop Channel"
            >
              <ComputerDesktopIcon className="w-5 h-5" />
            </button>
          </div>
        )}
        {/* Import button - Hidden for translators on English page */}
        {!(isTranslator && langId === 'en') && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={isSysAdmin ? (importFormat === 'zip' ? '.zip' : '.xlsx,.XLSX') : '.xlsx,.XLSX'}
              onChange={handleImport}
              className="hidden"
              id="import-translations-input"
              disabled={isImporting}
            />
            {isSysAdmin ? (
              <div className="relative inline-block" ref={importMenuRef}>
                <button
                  onClick={() => setShowImportMenu(!showImportMenu)}
                  disabled={isImporting}
                  className={`p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 ${
                    isImporting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={`Import ${importFormat.toUpperCase()}`}
                >
                  <DocumentArrowUpIcon className="w-5 h-5" />
                  <ChevronDownIcon className="w-3 h-3" />
                </button>
                {showImportMenu && (
                  <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <button
                      onClick={() => {
                        setImportFormat('zip');
                        setShowImportMenu(false);
                        // Update file input accept attribute
                        if (fileInputRef.current) {
                          fileInputRef.current.setAttribute('accept', '.zip');
                          // Trigger file input
                          setTimeout(() => {
                            fileInputRef.current?.click();
                          }, 100);
                        }
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        importFormat === 'zip' ? 'bg-gray-100 dark:bg-gray-700' : ''
                      }`}
                    >
                      Import ZIP
                    </button>
                    <button
                      onClick={() => {
                        setImportFormat('excel');
                        setShowImportMenu(false);
                        // Update file input accept attribute
                        if (fileInputRef.current) {
                          fileInputRef.current.setAttribute('accept', '.xlsx,.XLSX');
                          // Trigger file input
                          setTimeout(() => {
                            fileInputRef.current?.click();
                          }, 100);
                        }
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        importFormat === 'excel' ? 'bg-gray-100 dark:bg-gray-700' : ''
                      }`}
                    >
                      Import Excel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <label
                htmlFor="import-translations-input"
                className={`p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isImporting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                title="Import Excel"
              >
                <DocumentArrowUpIcon className="w-5 h-5" />
              </label>
            )}
          </>
        )}
        {/* Namespace export - Only for sysadmins */}
        {isSysAdmin && (
          <div className="relative" ref={exportNamespaceMenuRef}>
            <button
              onClick={() => setShowExportNamespaceMenu(!showExportNamespaceMenu)}
              disabled={!hasTranslations || !isFromDB[selectedNamespace]}
              className="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              title="Export Current Namespace"
            >
              <DocumentArrowDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <ChevronDownIcon className="w-3 h-3 text-gray-600 dark:text-gray-400" />
            </button>
            {showExportNamespaceMenu && (
              <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <button
                  onClick={() => handleExport(false, 'zip')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Export ZIP
                </button>
                <button
                  onClick={() => handleExport(false, 'excel')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Export Excel
                </button>
              </div>
            )}
          </div>
        )}
        <div className="relative" ref={exportAllMenuRef}>
          {isSysAdmin ? (
            <>
              <button
                onClick={() => setShowExportAllMenu(!showExportAllMenu)}
                disabled={!hasTranslations}
                className="p-2 bg-governate-500 text-white rounded-lg hover:bg-governate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                title={`Export All (${langId?.toUpperCase()})`}
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                <ChevronDownIcon className="w-3 h-3" />
              </button>
              {showExportAllMenu && (
                <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <button
                    onClick={() => handleExport(true, 'zip')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Export All ZIP
                  </button>
                  <button
                    onClick={() => handleExport(true, 'excel')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Export All Excel
                  </button>
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => handleExport(true)}
              disabled={!hasTranslations}
              className="p-2 bg-governate-500 text-white rounded-lg hover:bg-governate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={`Export All (${langId?.toUpperCase()})`}
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );

    setPageTitle(`Translation Management - ${langId?.toUpperCase()}`, undefined, {
      customNode,
      rightSideButtons,
    });

    return () => {
      setPageTitle('', undefined, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langId, channel, hasTranslations, isFromDB, selectedNamespace, isImporting, isSysAdmin]);

  return (
    <div className="space-y-6">

      {/* Namespace Dropdown */}
      <div className="flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700">Namespace:</label>
        <div className="relative">
          <select
            value={selectedNamespace}
            onChange={(e) => {
              // Reset edited values when switching namespaces (discard unsaved changes)
              const newNs = e.target.value;
              setSelectedNamespace(newNs);
              // Initialize edited state to match current translations (or empty if not loaded)
              setEditedTranslations({
                ...editedTranslations,
                [newNs]: translations[newNs] || {},
              });
            }}
            className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 cursor-pointer"
          >
            {namespaces.map((ns) => (
              <option key={ns} value={ns}>
                {ns.charAt(0).toUpperCase() + ns.slice(1)}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* Translation Editor */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedNamespace.charAt(0).toUpperCase() + selectedNamespace.slice(1)} Translations
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {isEditable ? (
                  <span className="text-green-600">✓ Editable (loaded from database)</span>
                ) : (
                          <span className="text-gray-500">Read-only (not in database - import to edit)</span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">
                {flatTranslations.length} keys
              </span>
              {isEditable && hasChanges() && (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-governate-500 text-white rounded-lg hover:bg-governate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {flatTranslations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No translations found for this namespace</p>
              </div>
            ) : (
              flatTranslations.map(({ key, value }) => (
                <div key={key} className="border-b border-gray-100 pb-4 last:border-b-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {key}
                  </label>
                  {isEditable ? (
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleValueChange(key, e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      placeholder="(empty)"
                    />
                  ) : (
                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 min-h-[2.5rem]">
                      {value || <span className="text-gray-400 italic">(empty)</span>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationDetail;
